// Wraps pure question-handlers.js with hook-owned state (questionEditIndex,
// questionToDelete, questionsToDelete). Bakes in surveyQuestions.length for
// addQuestion and collapses clone+insert into one call. moveQuestionUp/Down
// and reindexAfterDelete are re-exports of the pure handlers (they need
// form values/swap from JSX scope, no hook state). surveyQuestionsMutate
// accepted for future optimistic-update migration.

import { useState } from "react";

import {
    addQuestion as pureAddQuestion,
    cloneQuestion as pureCloneQuestion,
    moveQuestionUp as pureMoveQuestionUp,
    moveQuestionDown as pureMoveQuestionDown,
    reindexAfterDelete as pureReindexAfterDelete,
} from "../handlers/question-handlers";

export function useQuestionManager({
    survey,
    surveyQuestions,
    surveyQuestionsMutate,
} = {}) {
    const [questionEditIndex, setQuestionEditIndex] = useState(undefined);
    const [questionToDelete, setQuestionToDelete] = useState(undefined);
    const [questionsToDelete, setQuestionsToDelete] = useState([]);

    // Wraps pureAddQuestion with surveyQuestions.length so JSX call sites
    // don't repeat the fallback. Returns the new question so the caller
    // can decide whether to also open the settings dialog, etc.
    const addQuestion = (questions, appendCallback) =>
        pureAddQuestion(questions, appendCallback, surveyQuestions?.length || 0);

    // Wraps pureCloneQuestion and performs the field-array insert so JSX
    // call sites stay one-liners. Matches the original handleCloneQuestion
    // behavior in build.js.
    const cloneQuestion = (question, index, insertCallback) => {
        const result = pureCloneQuestion(question, index);
        insertCallback(result.insertIndex, result.clonedQuestion);
    };

    // The pure handlers take form state in their args; no state to bind.
    const moveQuestionUp = pureMoveQuestionUp;
    const moveQuestionDown = pureMoveQuestionDown;

    // Sets questionToDelete so DeleteQuestionDialog knows which index to
    // remove. Opening the dialog is the caller's responsibility because
    // dialog state is not owned by this hook.
    const deleteQuestion = (index) => setQuestionToDelete(index);

    const reindexAfterDelete = pureReindexAfterDelete;

    return {
        questionEditIndex,
        setQuestionEditIndex,
        questionToDelete,
        setQuestionToDelete,
        questionsToDelete,
        setQuestionsToDelete,
        addQuestion,
        cloneQuestion,
        moveQuestionUp,
        moveQuestionDown,
        deleteQuestion,
        reindexAfterDelete,
    };
}
